import { Notify } from "@/components/ui/notify";
import type { IndicatorValueResponse } from "@/types/indicators.types";

interface IndicatorValueCardProps {
  value: IndicatorValueResponse | null;
  isCalculating: boolean;
}

/**
 * Spec 082 — o número, ANTES de salvar. Duas regras que a tela erra na prática:
 *
 * · **sem base** é escrito em texto, nunca como `0%` — zero afirmaria que a fábrica não aproveitou
 *   nada, e é uma afirmação diferente de "não houve o que medir";
 * · o valor **não é capado em 100%**: acima disso é consumo de estoque, sinal legítimo, do mesmo
 *   jeito que o OEE não é capado.
 */
export function IndicatorValueCard({ value, isCalculating }: IndicatorValueCardProps) {
  if (isCalculating) {
    return <div className="h-16 rounded-md bg-bg-muted-rd motion-safe:animate-pulse" />;
  }
  if (!value) {
    return (
      <Notify.Empty
        title="Sem valor ainda"
        description="Monte pelo menos uma frase de cada lado para ver o número."
      />
    );
  }

  if (value.reason !== "OK") {
    return (
      <Notify.Banner
        variant="warning"
        title={value.reason === "NO_BASE" ? "Sem base" : "Base zerada"}
        description={
          value.reason === "NO_BASE"
            ? "Nenhuma frase do denominador tem registro no período escolhido."
            : "O denominador tem registro, mas soma zero. Não há como dividir."
        }
      />
    );
  }

  const acima = (value.value ?? 0) > 100;
  return (
    <div className="rounded-md border border-border-default p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-2xl font-semibold text-text-primary">
          {value.value!.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%
        </span>
        {value.target !== null ? (
          <span className="text-[12px] text-text-secondary">
            meta {value.target.toLocaleString("pt-BR")}%
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-[12px] text-text-secondary">
        {value.numerator?.toLocaleString("pt-BR")} de {value.denominator?.toLocaleString("pt-BR")}{" "}
        {value.baseUnit}
      </p>
      {acima ? (
        <p className="mt-1 text-[12px] text-text-secondary">
          Acima de 100%: saiu mais do que entrou no período. Costuma ser consumo de estoque.
        </p>
      ) : null}
    </div>
  );
}
